import * as React from 'react';

import { ArticleNavItem, ItemLinkComponent } from './types';

export interface SidebarContextProps {
  collapsed: boolean;
  ItemLinkComponent: ItemLinkComponent;
  activeArticleId?: string;
  onSelectArticle?: (id: string) => void;
  onExpandArticle?: (id: string) => void;
  onSearchArticles?: (value: string) => void;
  onAddArticle?: (parentId: string | null) => Promise<ArticleNavItem>;
  onMoreActions?: (articleId: string) => void;
  canMoveArticle?: (move: { id: string; parentId: string | null; index: number }) => boolean;
  onMoveArticle?: (moved: { id: string; parentId: string | null; index: number }) => void | Promise<void>;
}

export interface SidebarContextValue extends SidebarContextProps {
  handleSearchArticles: (value: string) => void;
  isSearching: boolean;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  /** Set when user selects from search results; tree uses this until activeArticleId catches up */
  lastSelectedArticleId: string | null;
  setLastSelectedArticleId: React.Dispatch<React.SetStateAction<string | null>>;
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

export function SidebarProvider({
  collapsed,
  ItemLinkComponent,
  activeArticleId,
  onSelectArticle,
  onExpandArticle,
  onSearchArticles,
  onAddArticle,
  onMoreActions,
  canMoveArticle,
  onMoveArticle,
  children,
}: React.PropsWithChildren<SidebarContextProps>) {
  const [isSearching, setIsSearching] = React.useState<boolean>(false);
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [lastSelectedArticleId, setLastSelectedArticleId] = React.useState<string | null>(null);

  const handleSearchArticles = React.useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      setIsSearching(trimmedValue !== '');
      onSearchArticles?.(value);
    },
    [onSearchArticles],
  );

  const contextValue = React.useMemo<SidebarContextValue>(
    () => ({
      collapsed,
      ItemLinkComponent,
      activeArticleId,
      onSearchArticles,
      onSelectArticle,
      onExpandArticle,
      onAddArticle,
      onMoreActions,
      canMoveArticle,
      onMoveArticle,
      isSearching,
      searchValue,
      setSearchValue,
      lastSelectedArticleId,
      setLastSelectedArticleId,
      handleSearchArticles,
    }),
    [
      isSearching,
      searchValue,
      lastSelectedArticleId,
      collapsed,
      ItemLinkComponent,
      activeArticleId,
      onSelectArticle,
      onExpandArticle,
      onSearchArticles,
      onAddArticle,
      onMoreActions,
      canMoveArticle,
      onMoveArticle,
      handleSearchArticles,
    ],
  );

  return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>;
}

export function useSidebar() {
  const sidebarContext = React.useContext(SidebarContext);

  if (!sidebarContext) throw new Error('useSidebar must be used within SidebarProvider');

  return sidebarContext;
}
